import {
  ComposerAddAttachment,
  ComposerAttachments,
  UserMessageAttachments,
  UserMessageMediaParts,
} from '@/client/components/assistant-ui/attachment';
import { MarkdownText } from '@/client/components/assistant-ui/markdown-text';

import { ToolFallback } from '@/client/components/assistant-ui/tool-fallback';
import { TooltipIconButton } from '@/client/components/assistant-ui/tooltip-icon-button';
import { Button } from '@/client/components/ui/button';
import { Textarea } from '@/client/components/ui/textarea';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { HIDDEN_CONTEXT_MARKER } from '@/client/lib/chatApi';
import { cn } from '@/client/lib/utils';
import {
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
  useMessage,
  useThread,
  useThreadList,
  useThreadRuntime,
} from '@assistant-ui/react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GitBranchIcon,
  GlobeIcon,
  LockIcon,
  SendIcon,
  SparklesIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  XIcon,
} from 'lucide-react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSuggestions } from '@/client/lib/suggestionsContext';
import { useSkills, type SkillInfo } from '@/client/lib/useSkills';
import {
  SlashCommandMenu,
  filterSkills,
  findReferencedSkills,
  getSlashMenuState,
  tokenEndingAt,
  type SlashMenuState,
} from '@/client/components/assistant-ui/SlashCommandMenu';
import { useUserName } from '@/client/lib/userNameContext';
import { useLangGraphInterruptState } from '@assistant-ui/react-langgraph';
import { MessageArtifacts } from '@/client/components/chat/MessageArtifacts';
import { StreamingStatus } from '@/client/components/chat/StreamingStatus';
import { ProgressTracker } from '@/client/components/ui/progress-tracker';
import { ToolCallsBlock } from '@/client/components/assistant-ui/tool-calls-block';
import { usePlan } from '@/client/lib/planContext';
import { TaskReview, useIsTaskReviewMode } from '@/client/components/chat/TaskReview';
import {
  IntegrationSetup,
  useIsIntegrationSetupMode,
} from '@/client/components/chat/IntegrationSetup';
import { UserQuestion, useIsUserQuestionMode } from '@/client/components/chat/UserQuestion';
import { CelebrationOverlay } from '@/client/components/chat/CelebrationCard';
import { isInterruptionLikeStreamError } from '@/client/lib/streamInterceptor';
import { useStreamingStatus } from '@/client/lib/streamingStatusContext';
import { cancelTrackedRunsForThread } from '@/client/lib/chatApi';
import { getLogicalTailMessageId } from '@/client/lib/toolStatus';

// ---------------------------------------------------------------------------
// Tool visibility context
// ---------------------------------------------------------------------------

export const StoppingMessageIdContext = createContext<string | null>(null);

interface ThreadProps {
  visibility?: 'public' | 'private';
  onVisibilityChange?: (v: 'public' | 'private') => void;
}

export const Thread: FC<ThreadProps> = ({ visibility, onVisibilityChange }) => {
  const [stoppingMessageId, setStoppingMessageId] = useState<string | null>(null);
  const threadMessages = useThread((s) => s.messages);

  const isRunning = useThread((s) => s.isRunning);
  const prevRunningRef = useRef(isRunning);
  useEffect(() => {
    if (!prevRunningRef.current && isRunning) {
      // Clear the optimistic stop marker when a new/continued run starts.
      setStoppingMessageId(null);
    }
    prevRunningRef.current = isRunning;
  }, [isRunning]);

  const currentThreadId = useAuiState(
    ({ threadListItem }) => threadListItem.externalId ?? threadListItem.remoteId ?? null,
  );

  const handleStop = useCallback(() => {
    setStoppingMessageId(getLogicalTailMessageId(threadMessages));
    if (!currentThreadId) return;
    void cancelTrackedRunsForThread(currentThreadId).catch((err) => {
      console.error('Failed to cancel active backend runs:', err);
    });
  }, [threadMessages, currentThreadId]);

  // Hide normal composer when task review is in interactive mode (has its own input)
  const isTaskReviewMode = useIsTaskReviewMode();
  const isIntegrationSetupMode = useIsIntegrationSetupMode();
  const isUserQuestionMode = useIsUserQuestionMode();

  return (
    <StoppingMessageIdContext.Provider value={stoppingMessageId}>
      <ThreadPrimitive.Root
        className="aui-root aui-thread-root @container relative flex h-full flex-col"
        style={{
          ['--thread-max-width' as string]: '44rem',
        }}
      >
        <CelebrationOverlay />
        <ThreadPrimitive.Viewport
          turnAnchor="bottom"
          autoScroll
          className="aui-thread-viewport relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth px-4 pt-4 pb-3"
        >
          <AuiIf condition={(s) => s.thread.isEmpty}>
            <ThreadWelcome />
          </AuiIf>

          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              EditComposer,
              AssistantMessage,
              SystemMessage: () => null,
            }}
          />

          <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 z-10 mt-auto flex w-full justify-center overflow-visible bg-background">
            <ThreadScrollToBottom />
          </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Viewport>

        {/* Footer outside scroll container — prevents layout jumps when plan/assets toggle */}
        <div className="shrink-0 bg-background px-4 pb-4 md:pb-6">
          <div className="mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-3">
            <PlanOrStreamingStatus onStop={handleStop} />
            {isIntegrationSetupMode ? (
              <IntegrationSetup />
            ) : isTaskReviewMode ? (
              <TaskReview />
            ) : isUserQuestionMode ? (
              <UserQuestion />
            ) : (
              <Composer visibility={visibility} onVisibilityChange={onVisibilityChange} />
            )}
          </div>
        </div>
      </ThreadPrimitive.Root>
    </StoppingMessageIdContext.Provider>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

/**
 * Renders ProgressTracker when a plan exists in thread state,
 * otherwise falls back to the StreamingStatus spinner bar.
 */
const PlanOrStreamingStatus: FC<{ onStop?: () => void }> = ({ onStop }) => {
  const { plan } = usePlan();
  const threadIsRunning = useThread((s) => s.isRunning);
  const { getStatus, isThreadStreamActive, isJoinedThread } = useStreamingStatus();
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);
  const currentThread = threadItems[mainThreadId];
  const currentThreadId = currentThread?.externalId ?? currentThread?.remoteId;
  const streamingStatus = getStatus(currentThreadId);
  const isRunning = threadIsRunning || !!streamingStatus || isThreadStreamActive(currentThreadId);
  const joined = isJoinedThread(currentThreadId);

  // Detect any interrupt (task_review or legacy HITL)
  const interruptState = useLangGraphInterruptState();
  const interruptValue = interruptState?.value as Record<string, unknown> | undefined;
  const isInterrupted = !!(
    interruptValue?.type === 'task_review' ||
    interruptValue?.type === 'integration_setup' ||
    interruptValue?.type === 'user_question' ||
    (Array.isArray(
      (interruptValue as { action_requests?: unknown[] } | undefined)?.action_requests,
    ) &&
      (interruptValue as { action_requests: unknown[] }).action_requests.length > 0)
  );

  if (plan && plan.steps.length > 0) {
    return (
      <ProgressTracker
        plan={{
          title: plan.title,
          steps: plan.steps,
          startedAt: plan.started_at,
          completedAt: plan.completed_at,
        }}
        streamingStatus={streamingStatus}
        isRunning={isRunning}
        isJoined={joined}
        isInterrupted={isInterrupted}
        onStop={joined ? undefined : onStop}
      />
    );
  }

  return <StreamingStatus onStop={onStop} />;
};

const ThreadWelcome: FC = () => {
  const firstName = useUserName();

  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
        <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
          <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in font-semibold text-2xl duration-200">
            Hello{firstName ? `, ${firstName}` : ''}!
          </h1>
          <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in text-muted-foreground text-xl delay-75 duration-200">
            How can I help you today?
          </p>
        </div>
      </div>
      <ThreadSuggestions />
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  const suggestions = useSuggestions();
  const threadRuntime = useThreadRuntime();

  return (
    <div className="aui-thread-welcome-suggestions grid w-full grid-cols-2 gap-2 pb-4">
      {suggestions.map((s, i) => (
        <div
          key={i}
          className="aui-thread-welcome-suggestion-display min-w-0 fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
        >
          <Button
            variant="ghost"
            className="aui-thread-welcome-suggestion flex h-auto min-w-0 w-full flex-col items-start justify-start gap-1 rounded-2xl border px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
            onClick={() => {
              threadRuntime.append({
                role: 'user',
                content: [{ type: 'text', text: s.prompt }],
              });
            }}
          >
            <span className="aui-thread-welcome-suggestion-text-1 block w-full whitespace-normal break-words font-medium leading-tight">
              {s.title}
            </span>
            <span className="aui-thread-welcome-suggestion-text-2 block w-full whitespace-normal break-words text-muted-foreground leading-tight">
              {s.label}
            </span>
          </Button>
        </div>
      ))}
    </div>
  );
};

const Composer: FC<{
  visibility?: 'public' | 'private';
  onVisibilityChange?: (v: 'public' | 'private') => void;
}> = ({ visibility, onVisibilityChange }) => {
  const aui = useAui();
  const composerIsEmpty = useAuiState((s) => s.composer.isEmpty);
  const composerText = useAuiState((s) => s.composer.text);
  const joined = useIsJoinedThread();
  const [skillBranch] = useState(readSkillBranch);
  const { skills } = useSkills(skillBranch);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [menuState, setMenuState] = useState<SlashMenuState | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const knownSkillSet = useMemo(() => new Set(skills.map((s) => s.name)), [skills]);

  const filtered = useMemo(
    () => (menuState ? filterSkills(skills, menuState.query) : []),
    [menuState, skills],
  );

  // Keep the menu state in sync with the textarea as the user types or moves
  // the caret. Recompute on every text change *and* on selection changes —
  // mouse clicks and arrow keys move the caret without changing the text.
  const recomputeMenuState = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta || ta.disabled) return;
    const caret = ta.selectionStart ?? composerText.length;
    const next = getSlashMenuState(composerText, caret);
    setMenuState((prev) => {
      if (!next && !prev) return prev;
      if (next && prev && next.start === prev.start && next.query === prev.query) return prev;
      return next;
    });
  }, [composerText]);

  useEffect(() => {
    recomputeMenuState();
  }, [recomputeMenuState]);

  // Reset selection to the top whenever the matching set might have shifted
  // (a different `/` token was opened, or the query changed within the same one).
  useEffect(() => {
    setHighlightIndex(0);
  }, [menuState?.start, menuState?.query]);

  const closeMenu = useCallback(() => setMenuState(null), []);

  const insertSkill = useCallback(
    (skill: SkillInfo) => {
      if (!menuState) return;
      const before = composerText.slice(0, menuState.start);
      const after = composerText.slice(menuState.start + 1 + menuState.query.length);
      // Insert as plain `/skill-name ` so the textarea stays readable. The
      // ``<gaia-skill .../>`` delimiter is only stamped in at send time —
      // see `sendWithMarkers` below. The trailing space lets the user keep
      // typing without manually breaking the slash token; we skip it only
      // when the next char is already whitespace.
      const sep = after.startsWith(' ') ? '' : ' ';
      const insert = `/${skill.name}${sep}`;
      const newText = `${before}${insert}${after}`;
      aui.composer().setText(newText);
      closeMenu();
      const cursorAt = before.length + insert.length;
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(cursorAt, cursorAt);
      });
    },
    [aui, closeMenu, composerText, menuState],
  );

  // Wraps composer.send() with the slash-skill body embed. For each
  // ``/skill-name`` in the message we append ``<skill name="X">{body}</skill>``
  // under HIDDEN_CONTEXT_MARKER, the same delimiter `withPageContext` uses to
  // hide the page-context note from the rendered message. The agent sees the
  // full body, the chat history shows just the user's plain text with chip
  // styling on the slash tokens.
  const sendWithMarkers = useCallback(() => {
    const composer = aui.composer();
    const text = composer.getState().text;
    if (!text.trim()) return;
    const referenced = findReferencedSkills(text, skills);
    if (referenced.length > 0) {
      const block = referenced
        .map((s) => `<skill name="${s.name}">\n${s.body}\n</skill>`)
        .join('\n\n');
      const augmented = `${text}${HIDDEN_CONTEXT_MARKER}Execute these skills:\n\n${block}`;
      composer.setText(augmented);
    }
    composer.send();
  }, [aui, skills]);

  // Atomic-delete a known /skill-name when Backspace is pressed at its right
  // edge. Treats the slash token like a single chip in mention-style UIs:
  // one keystroke removes the whole thing, including the trailing space we
  // added on insertion.
  const handleBackspaceToken = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (
        e.key !== 'Backspace' ||
        e.shiftKey ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        e.nativeEvent.isComposing
      ) {
        return false;
      }
      const ta = textareaRef.current;
      if (!ta || ta.selectionStart !== ta.selectionEnd) return false;
      const caret = ta.selectionStart;
      const tok = tokenEndingAt(composerText, caret, knownSkillSet);
      if (!tok) return false;
      e.preventDefault();
      const trailingSpace = composerText[caret] === ' ' ? 1 : 0;
      const newText = composerText.slice(0, tok.start) + composerText.slice(caret + trailingSpace);
      aui.composer().setText(newText);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(tok.start, tok.start);
      });
      return true;
    },
    [aui, composerText, knownSkillSet],
  );

  // We disable native Enter-to-send (`submitMode="none"`) on the input so the
  // slash-substitution pass below always runs before the message is sent.
  // That also lets us keep the original double-texting behavior (sending
  // while a run is in flight) and the joined-thread block in one place.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (menuState && filtered.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightIndex((i) => (i + 1) % filtered.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightIndex((i) => (i - 1 + filtered.length) % filtered.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertSkill(filtered[highlightIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMenu();
          return;
        }
      }
      if (handleBackspaceToken(e)) return;
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (joined) return;
        if (composerIsEmpty) return;
        sendWithMarkers();
      }
    },
    [closeMenu, composerIsEmpty, filtered, handleBackspaceToken, highlightIndex, insertSkill, joined, menuState, sendWithMarkers],
  );

  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
      {menuState && (
        <SlashCommandMenu
          skills={skills}
          state={menuState}
          highlightIndex={highlightIndex}
          onSelect={insertSkill}
          onHover={setHighlightIndex}
        />
      )}
      <ComposerPrimitive.AttachmentDropzone className="aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-input bg-background px-1 pt-2 outline-none transition-shadow has-[textarea:focus-visible]:border-[hsl(262_60%_55%)] has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-[hsl(262_60%_55%)] data-[dragging=true]:border-[hsl(262_60%_55%)] data-[dragging=true]:border-dashed data-[dragging=true]:bg-accent/50">
        <ComposerAttachments />
        <ComposerPrimitive.Input
          ref={textareaRef}
          placeholder={joined ? 'Agent is running elsewhere' : 'Send a message... (type / for skills)'}
          disabled={joined}
          className="aui-composer-input mb-1 max-h-32 min-h-14 w-full resize-none bg-transparent px-4 pt-2 pb-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          rows={1}
          autoFocus
          aria-label="Message input"
          submitMode="none"
          onKeyDown={handleKeyDown}
          onSelect={recomputeMenuState}
          onClick={recomputeMenuState}
          onBlur={closeMenu}
        />
        <ComposerAction
          visibility={visibility}
          onVisibilityChange={onVisibilityChange}
          onSend={sendWithMarkers}
        />
      </ComposerPrimitive.AttachmentDropzone>
    </ComposerPrimitive.Root>
  );
};

function readSkillBranch(): string | null {
  try {
    // Embed stores skill branch directly
    const embed = localStorage.getItem('gaia-embed-skill-branch');
    if (embed) return embed;
    // Main app stores it in agent metadata (gaia agent)
    const raw = localStorage.getItem('gaia-agent-metadata-gaia');
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, string>;
      if (parsed.skills_branch) return parsed.skills_branch;
    }
    return null;
  } catch {
    return null;
  }
}

const ComposerAction: FC<{
  visibility?: 'public' | 'private';
  onVisibilityChange?: (v: 'public' | 'private') => void;
  onSend: () => void;
}> = ({ visibility, onVisibilityChange, onSend }) => {
  const isPrivate = visibility !== 'public';
  const isEmpty = useThread((s) => s.messages.length === 0);
  const [skillBranch] = useState(readSkillBranch);
  return (
    <div className="aui-composer-action-wrapper relative mx-2 mb-2 flex items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <ComposerAddAttachment />
        {visibility && onVisibilityChange && isEmpty && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onVisibilityChange(isPrivate ? 'public' : 'private')}
                className="font-primary flex shrink-0 cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {isPrivate ? <LockIcon className="size-3.5" /> : <GlobeIcon className="size-3.5" />}
                <span>{isPrivate ? 'Private' : 'Public'}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {isPrivate
                ? 'Switch to public — visible to all account users'
                : 'Switch to private — only visible to you'}
            </TooltipContent>
          </Tooltip>
        )}
        {skillBranch && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex min-w-0 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
                <GitBranchIcon className="size-3 shrink-0" />
                <span className="truncate">{skillBranch}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{skillBranch}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <SendButton onSend={onSend} />
    </div>
  );
};

/**
 * Custom Send button that allows sending while the thread is running (double-texting).
 * ComposerPrimitive.Send disables itself when isRunning, so we bypass it.
 */
const SendButton: FC<{ onSend: () => void }> = ({ onSend }) => {
  const joined = useIsJoinedThread();
  const disabled = useAuiState((s) => !s.composer.isEditing || s.composer.isEmpty) || joined;
  return (
    <TooltipIconButton
      tooltip="Send message"
      side="top"
      variant="default"
      size="icon"
      className="aui-composer-send font-primary size-8 rounded-full bg-[hsl(262_60%_55%)] text-white hover:bg-[hsl(262_60%_55%)] focus-visible:ring-[hsl(262_60%_55%)]"
      aria-label="Send message"
      disabled={disabled}
      onClick={onSend}
    >
      <ArrowUpIcon className="aui-composer-send-icon size-4" />
    </TooltipIconButton>
  );
};

/**
 * Custom error display that only renders when the message has a displayable
 * error string.  assistant-ui may set status.error to opaque objects (e.g.
 * from interrupted streams during double-texting) which render as
 * "[object Object]" — we suppress those.
 */
const MessageError: FC = () => {
  const isStreamActive = useIsStreamActive();
  const errorValue = useAuiState((s) => {
    const status = s.message.status;
    if (status && 'error' in status) return status.error;
    return undefined;
  });

  const text =
    typeof errorValue === 'string'
      ? errorValue
      : errorValue && typeof errorValue === 'object' && 'message' in errorValue
        ? String((errorValue as { message: unknown }).message)
        : null;

  if (!text) return null;
  if (isStreamActive) return null;
  if (isInterruptionLikeStreamError(text)) return null;

  return (
    <div className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
      <p className="aui-message-error-message line-clamp-2">{text}</p>
    </div>
  );
};

function useCurrentExternalThreadId(): string | undefined {
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);
  const currentThread = threadItems[mainThreadId];
  return currentThread?.externalId ?? currentThread?.remoteId;
}

function useIsStreamActive(): boolean {
  const isRunning = useThread((s) => s.isRunning);
  const { getStatus, isThreadStreamActive } = useStreamingStatus();
  const currentThreadId = useCurrentExternalThreadId();
  const streamStatus = getStatus(currentThreadId);

  return isRunning || !!streamStatus || isThreadStreamActive(currentThreadId);
}

function useIsJoinedThread(): boolean {
  const { isJoinedThread } = useStreamingStatus();
  const currentThreadId = useCurrentExternalThreadId();
  return isJoinedThread(currentThreadId);
}

/**
 * In messages with tool calls, only show the last text part (the final answer
 * or the currently streaming text). Intermediate text between tool calls is
 * hidden — it's preserved in the ToolCallsBlock's reasoning section instead.
 * Messages without tool calls render all text normally.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SmartText: any = (props: Record<string, unknown>) => {
  const aui = useAui();
  const partMeta = aui.part;
  const partIndex = partMeta?.query?.type === 'index' ? partMeta.query.index : -1;

  const shouldHide = useAuiState((s) => {
    const parts = s.message.parts;
    if (!parts) return false;
    const hasToolCalls = parts.some((p) => p.type === 'tool-call');
    if (!hasToolCalls) return false;

    // Show only the last text part — that's the final answer or current stream
    let lastTextIdx = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].type === 'text') {
        lastTextIdx = i;
        break;
      }
    }
    return partIndex !== lastTextIdx;
  });

  if (shouldHide) return null;
  return <MarkdownText {...props} />;
};

/**
 * Tool call parts are always hidden from MessagePrimitive.Parts.
 * They render via ToolCallsBlock instead (compact rows in a collapsible block).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HiddenToolFallback: any = Object.assign(() => null, {
  Root: ToolFallback.Root,
  Trigger: ToolFallback.Trigger,
  Content: ToolFallback.Content,
  Args: ToolFallback.Args,
  Result: ToolFallback.Result,
  Error: ToolFallback.Error,
});

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      {/* Collapsible reasoning block — before the final answer */}
      <div className="px-2">
        <ToolCallsBlock />
      </div>

      <div className="aui-assistant-message-content wrap-break-word px-2 text-sm text-foreground leading-relaxed">
        <MessagePrimitive.Parts
          components={{
            Text: SmartText,
            tools: { Fallback: HiddenToolFallback },
          }}
        />
        <MessageError />
      </div>

      <MessageArtifacts />

      <div className="aui-assistant-message-footer mt-1 ml-2 flex items-center gap-2">
        <BranchPicker />
        <MessageFeedback />
      </div>
    </MessagePrimitive.Root>
  );
};

/** Concatenated visible text across all text parts (strips HIDDEN_CONTEXT_MARKER tail). */
function getVisibleUserText(parts: readonly { type: string; text?: string }[] | undefined): string {
  if (!parts) return '';
  return parts
    .filter((p) => p.type === 'text')
    .map((p) => (p.text ?? '').split(HIDDEN_CONTEXT_MARKER)[0])
    .join('');
}

/** Word-boundary anchored slash-token matcher used for chip rendering. */
const USER_SLASH_RE = /(^|\s)\/([a-zA-Z][\w-]*)/g;

/** Splits visible user text into plain text and known-skill segments. */
function renderUserMessageText(
  text: string,
  known: Set<string>,
): Array<string | { skill: string }> {
  if (known.size === 0 || !text.includes('/')) return [text];
  const out: Array<string | { skill: string }> = [];
  let cursor = 0;
  for (const m of text.matchAll(USER_SLASH_RE)) {
    const lead = m[1] ?? '';
    const name = m[2];
    if (!known.has(name)) continue;
    const tokenStart = (m.index ?? 0) + lead.length;
    const tokenEnd = tokenStart + 1 + name.length;
    if (tokenStart > cursor) out.push(text.slice(cursor, tokenStart));
    out.push({ skill: name });
    cursor = tokenEnd;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out.length ? out : [text];
}

/**
 * Strip hidden context (after HIDDEN_CONTEXT_MARKER) and render any
 * ``/skill-name`` tokens whose name is in the registry as inline chips.
 * Chips only appear in sent messages — the composer textarea stays plain
 * text by design.
 */
const UserText: FC = () => {
  const visible = useAuiState((s) => getVisibleUserText(s.message.parts));
  const [skillBranch] = useState(readSkillBranch);
  const { skills } = useSkills(skillBranch);
  const known = useMemo(() => new Set(skills.map((s) => s.name)), [skills]);
  if (!visible) return null;
  const parts = renderUserMessageText(visible, known);
  return (
    <p className="whitespace-pre-wrap">
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          <span key={i}>{p}</span>
        ) : (
          <SkillBadge key={i} name={p.skill} />
        ),
      )}
    </p>
  );
};

/**
 * Inline pill rendered in place of a known ``/skill-name`` token in a sent
 * user message. Uses the brand purple to keep skills visually distinct from
 * the amber skill-branch badge in the composer footer.
 */
const SkillBadge: FC<{ name: string }> = ({ name }) => (
  <span className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-[hsl(262_60%_55%)]/30 bg-[hsl(262_60%_55%)]/10 px-1.5 py-0.5 align-baseline text-xs font-medium text-[hsl(262_60%_55%)]">
    <SparklesIcon className="size-3 shrink-0" />
    <span>{name}</span>
  </span>
);

const UserMessage: FC = () => {
  const hasVisibleText = useAuiState((s) => getVisibleUserText(s.message.parts).trim().length > 0);

  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />
      <UserMessageMediaParts />

      {hasVisibleText && (
        <div className="aui-user-message-content-wrapper col-start-2 min-w-0">
          <div className="aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 text-sm text-foreground">
            <UserText />
          </div>
        </div>
      )}

      <div className="aui-user-message-footer col-span-full col-start-1 row-start-3 flex items-center justify-end gap-2">
        <BranchPicker className="aui-user-branch-picker -mr-1 justify-end" />
      </div>
    </MessagePrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">Update</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        'aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs',
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};

const FEEDBACK_PREFIX = 'I want to share a feedback to improve Gaia behavior: ';

const MessageFeedback: FC = () => {
  const { isLast } = useMessage();
  const isStreamActive = useIsStreamActive();
  const threadRuntime = useThreadRuntime();
  const [feedback, setFeedback] = useState<'up' | 'down' | 'submitted' | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  if (!isLast || isStreamActive) return null;

  const handleThumbsDown = () => {
    if (feedback === 'submitted') return;
    setFeedback('down');
    setShowInput(true);
  };

  const handleSubmit = () => {
    const text = feedbackText.trim();
    if (!text) return;
    threadRuntime.append({
      role: 'user',
      content: [{ type: 'text', text: `${FEEDBACK_PREFIX}${text}` }],
    });
    setFeedback('submitted');
    setShowInput(false);
    setFeedbackText('');
  };

  const handleCancel = () => {
    setShowInput(false);
    setFeedbackText('');
    setFeedback(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-0.5">
        <TooltipIconButton
          tooltip="Good response"
          variant="ghost"
          className={cn(
            'size-6 text-muted-foreground/50 hover:text-muted-foreground',
            feedback === 'up' && 'text-primary',
          )}
          onClick={() => setFeedback((prev) => (prev === 'up' ? null : 'up'))}
        >
          <ThumbsUpIcon className="size-3.5" />
        </TooltipIconButton>
        <TooltipIconButton
          tooltip="Bad response"
          variant="ghost"
          className={cn(
            'size-6 text-muted-foreground/50 hover:text-muted-foreground',
            (feedback === 'down' || feedback === 'submitted') && 'text-destructive',
          )}
          onClick={handleThumbsDown}
        >
          <ThumbsDownIcon className="size-3.5" />
        </TooltipIconButton>
      </div>
      {showInput && (
        <div className="flex flex-col gap-2 rounded-lg border border-border/50 p-3">
          <p className="text-muted-foreground text-xs font-medium">What went wrong?</p>
          <Textarea
            placeholder="Tell us what was incorrect or unhelpful…"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            className="min-h-[60px] resize-none text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div className="flex items-center justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleCancel}>
              <XIcon className="mr-1 size-3" />
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleSubmit}
              disabled={!feedbackText.trim()}
            >
              <SendIcon className="mr-1 size-3" />
              Send feedback
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
